/**
 * Risk Management dashboard — `/dashboard/risk-management`.
 * Port of the Power BI page "Risk Management v.2" (`plan/PBI_ERP.pbix`):
 * header (title + report-date, styled like the PnL page header for consistency —
 * chị Đẹp 2026-06-10), then the Supplier's Exposure matrix (rows = supplier,
 * columns = the embedded `d_exposure_template` Lv1/Lv2 groups, values = measure
 * `RskMgt`, format `#,0.00;(#,0.00)`).
 *
 * Table dùng template dataList (skill /convert-table, 2026-06-10): FilterBar
 * (global Search + Columns menu) + per-column ▽ filter + sort + resize +
 * pagination + freeze cột Supplier. Header giữ 2 tầng group Lv1/Lv2 của PBI —
 * tầng group build theo run các cột visible liền kề cùng `group` nên vẫn đúng
 * khi user ẩn/reorder cột.
 *
 * Data comes from `GET /dashboard/risk-management/supplier-exposure` (Report DB
 * view `public.v_pos_exposure`). All numbers — including the Total row (pinned
 * footer) and the report date — come from the API; nothing is computed
 * client-side (memory `feedback_api_view_source_of_truth`).
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert, Box, Button, CircularProgress, Divider, LinearProgress, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useMemo, useState, type CSSProperties } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../../components/BrandState'
import ResizeHandle from '../../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../../components/dataList'
import { useColumnWidths } from '../../../hooks/useColumnWidths'
import { titleCaseName } from '../../../utils/text'
import { useIsMobile } from '../../../hooks/useResponsive'
import { HIDE_ON_MOBILE_INLINE } from '../../../theme/responsive'
import {
  getTableSortSuffix,
  sortTableRows,
  toggleTableSort,
  type TableSortState,
} from '../../../utils/tableSort'
import { useLocalDatasheetColumns } from '../../../utils/datasheetColumns/useLocalDatasheetColumns'
import { FuturesColumnsMenu } from '../../futures/FuturesColumnsMenu'
import {
  type SupplierExposureRow,
  useGetSupplierExposureQuery,
} from '../../../store/api/dashboardApi'
import { ReportPageToolbar } from '../../report/reportToolbarSlot'
import { useReportZoom } from '../_shared/useReportZoom'

type RowKey = keyof SupplierExposureRow & string

interface ColumnDef {
  key: RowKey
  /** Label đầy đủ (group — sub) cho Columns menu + filter popover. */
  label: string
  /** Lv1 của `d_exposure_template` — undefined = cột Supplier (rowSpan 2 tầng). */
  group?: string
  /** Lv2 — hiển thị ở tầng header dưới. */
  sub: string
  kind?: 'number'
  align?: 'right'
  filter?: ColumnFilterKind
}

const NUM = { kind: 'number', align: 'right' } as const

// Cột theo đúng `d_exposure_template` (Lv1/Lv2, order_by) — xem
// `plan/RiskManagement_BE_Proposal.md`.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'supplierName', label: 'Supplier', sub: 'Supplier', filter: 'text' },
  { key: 'purchaseMt', label: 'Purchase — MT', group: 'Purchase', sub: 'MT', ...NUM },
  { key: 'toReceiveMt', label: 'To receive — MT', group: 'To receive (Total exposure)', sub: 'MT', ...NUM },
  { key: 'toReceiveUsd', label: 'To receive — USD', group: 'To receive (Total exposure)', sub: 'USD', ...NUM },
  { key: 'outrightMt', label: 'Outright exposure — MT', group: 'Outright exposure', sub: 'MT', ...NUM },
  { key: 'outrightUsd', label: 'Outright exposure — USD', group: 'Outright exposure', sub: 'USD', ...NUM },
  { key: 'ptbfMt', label: 'PTBF exposure — MT', group: 'PTBF exposure', sub: 'MT', ...NUM },
  { key: 'ptbfUsd', label: 'PTBF exposure — USD', group: 'PTBF exposure', sub: 'USD', ...NUM },
  { key: 'rm1NpeBalanceUnpaid', label: 'Risk Mitigation 1 — NPE Balance unpaid', group: 'Risk Mitigation 1', sub: 'NPE Balance unpaid', ...NUM },
  { key: 'rm1Cumulative', label: 'Risk Mitigation 1 — Cumulative 1', group: 'Risk Mitigation 1', sub: 'Cumulative 1', ...NUM },
  { key: 'rm2PtbfBalance', label: 'Risk Mitigation 2 — PBTF balance unpaid', group: 'Risk Mitigation 2', sub: 'PBTF balance unpaid', ...NUM },
  { key: 'rm2Cumulative', label: 'Risk Mitigation 2 — Cumulative 2', group: 'Risk Mitigation 2', sub: 'Cumulative 2', ...NUM },
  { key: 'rm3CargoOffloaded', label: 'Risk Mitigation 3 — Cargo offloaded /unpaid', group: 'Risk Mitigation 3', sub: 'Cargo offloaded /unpaid', ...NUM },
  { key: 'rm3Cumulative', label: 'Risk Mitigation 3 — Cumulative 3', group: 'Risk Mitigation 3', sub: 'Cumulative 3', ...NUM },
  { key: 'rm4CargoInCompound', label: 'Risk Mitigation 4 — Cargo in-compound /unpaid', group: 'Risk Mitigation 4', sub: 'Cargo in-compound /unpaid', ...NUM },
  { key: 'rm4Cumulative', label: 'Risk Mitigation 4 — Cumulative 4', group: 'Risk Mitigation 4', sub: 'Cumulative 4', ...NUM },
  { key: 'exposureLimitMt', label: 'Exposure Limit — MT', group: 'Exposure Limit', sub: 'MT', ...NUM },
  { key: 'exposureLimitUsd', label: 'Exposure Limit — USD', group: 'Exposure Limit', sub: 'USD', ...NUM },
  { key: 'roomMt', label: 'Room to buy — MT', group: 'Room to buy', sub: 'MT', ...NUM },
  { key: 'roomUsdWithRm2', label: 'Room to buy — USD (with risk mitigation 2)', group: 'Room to buy', sub: 'USD (with risk mitigation 2)', ...NUM },
  { key: 'roomUsdWithoutMitigation', label: 'Room to buy — USD (without mitigation)', group: 'Room to buy', sub: 'USD (without mitigation)', ...NUM },
]

/** Cột freeze khi scroll ngang — chỉ cột định danh Supplier. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['supplierName'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { supplierName: 180 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { supplierName: 140 }

// Size chữ đồng bộ các bảng dashboard PBI (`PnLTable` HEAD_SX + body cell,
// `LongShortMatrix` dùng cùng giá trị — chị Đẹp chốt 2026-06-10).
const HEAD_FONT_SX = {
  fontSize: '0.66rem',
  lineHeight: 1.15,
  py: 0.5,
  px: { xs: 0.5, xl: 0.75 },
} as const
const BODY_FONT_SX = {
  fontSize: '0.68rem',
  lineHeight: 1.25,
  py: 0.35,
  px: { xs: 0.5, xl: 0.75 },
} as const

// Visual skin (match Cashflows / reportTokens.ts) — hardcoded inline hex so we
// don't touch pnl/tokens.ts. Forest header, horizontal grid only, no zebra.
const RPT_FOREST = '#0b3633'
const RPT_GRID = '#e8ecef'
const RPT_BORDER = '#dfe3e8'
const RPT_NEG = '#d32f2f'
const RPT_SUMMARY_BG = '#e8f1ee'

/** Inline override applied to EVERY header cell — solid forest, white text. */
const HEAD_SKIN_SX = {
  backgroundImage: 'none',
  backgroundColor: RPT_FOREST,
  color: '#fff',
  fontWeight: 600,
} as const

const nf = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** PBI value format `#,0.00; (#,0.00)`: blank → '', âm → ngoặc (tô đỏ qua sx). */
const fmtMeasure = (v: number | null | undefined): { text: string; neg: boolean } => {
  if (v == null) return { text: '', neg: false }
  if (v < 0) return { text: `(${nf.format(Math.abs(v))})`, neg: true }
  return { text: nf.format(v), neg: false }
}

export default function RiskManagementPage() {
  const { data, isLoading, isFetching, isError, refetch } = useGetSupplierExposureQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const { zoom, control } = useReportZoom()
  const baseRows = useMemo(() => (data?.rows ?? []).filter((r) => !r.isTotal), [data])
  const totalRow = useMemo(() => (data?.rows ?? []).find((r) => r.isTotal) ?? null, [data])

  // Global Search
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()

  // Per-column filters (▽ trong header — session-only)
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: RowKey
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [sortState, setSortState] = useState<TableSortState<RowKey> | null>(null)

  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: TABLE_COLUMNS,
    storageKey: 'dashboard.risk-management.supplier-exposure',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('dashboard.risk-management')
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, TABLE_COLUMNS)
          && rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )
  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  // Tầng header group (Lv1): gộp run các cột visible LIỀN KỀ cùng `group` —
  // đúng cả khi user ẩn/reorder cột qua Columns menu. Cột Supplier (group
  // undefined) render rowSpan 2 ngay tại vị trí của nó.
  const headerRuns = useMemo(() => {
    const runs: { group: string | null; cols: ColumnDef[] }[] = []
    for (const col of visibleColumns) {
      const g = col.group ?? null
      const last = runs[runs.length - 1]
      if (last && g !== null && last.group === g) last.cols.push(col)
      else runs.push({ group: g, cols: [col] })
    }
    return runs
  }, [visibleColumns])

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const toggleSort = (key: RowKey) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const minTableWidth = Math.max(1100, visibleColumns.length * 84)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  /** Leaf header cell — sort + ▽ filter + resize; Supplier frozen (inline style §7.6). */
  const renderLeafHeaderCell = (col: ColumnDef, rowSpan?: number) => {
    const key = col.key
    const isFrozen = FROZEN_KEYS.has(key)
    const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
    const headerStyle: CSSProperties = {}
    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
    if (isFrozen) {
      headerStyle.position = 'sticky'
      headerStyle.top = 0
      headerStyle.left = 0
      headerStyle.zIndex = 100
    }
    return (
      <TableCell
        key={key}
        align={col.align}
        rowSpan={rowSpan}
        className="app-table-header-cell"
        onClick={() => toggleSort(key)}
        style={Object.keys(headerStyle).length ? headerStyle : undefined}
        sx={{
          ...HEAD_FONT_SX,
          ...HEAD_SKIN_SX,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          userSelect: 'text',
          ...(isFrozen ? {} : { position: 'relative' }),
        }}
      >
        {col.sub}
        {getTableSortSuffix(sortState, key)}
        <ColumnFilterIcon
          active={columnFilters.isActive(key)}
          onClick={(position) => setColPopover({ key, position })}
          ariaLabel={`Filter ${col.label}`}
        />
        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
      </TableCell>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Desktop: bounded height → table fills + internal scroll. Mobile/tablet:
        // auto height so the page grows with content and scrolls in the report
        // area (fit-to-device), mirroring FuturesPositionPage.
        height: { lg: '100%' },
        minHeight: 0,
        pb: { xs: 2, lg: 0 },
        // Không tự thêm padding — Layout `<main>` đã có p:{xs:0.75,sm:1,md:1.5}.
        // Đồng bộ outer margin với PnL/Long-Short để chuyển trang không lệch format.
        // Parent chain đã bounded (height 100%) → bảng fill flex thay vì clamp
        // viewport của `.app-dataframe` (skill /convert-table §7.9).
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      {/* Header — format đồng bộ PnL page: Paper outlined, title + report date 1 dòng.
          Portal NGUYÊN block lên block chung của ReportsPage khi nhúng. */}
      <ReportPageToolbar>
      <Paper
        variant="outlined"
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: 1,
          border: `1px solid ${RPT_BORDER}`,
          borderRadius: '8px',
          bgcolor: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '0.92rem', sm: '1rem' }, fontWeight: 900, color: '#1a1a1a', lineHeight: 1.15 }}>
            RISK MANAGEMENT
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: '#b28a3b', fontWeight: 600 }}>
            {data?.reportDate ? dayjs(data.reportDate).format('MMM DD, YYYY') : ''}
          </Typography>
          {/* Descriptor — khớp format block "LONG - SHORT" (dòng titleText sau ngày). */}
          <Typography sx={{ fontSize: '0.78rem', color: '#1a1a1a', fontWeight: 700 }}>
            Supplier&apos;s Exposure
          </Typography>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        {control}
        {/* Reload — refetch() ép gọi lại API kể cả khi args không đổi (pattern PnL page). */}
        <Tooltip title="Reload dashboard data">
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem' }}
            >
              Reload
            </Button>
          </span>
        </Tooltip>
      </Paper>
      </ReportPageToolbar>

      {isError && <Alert severity="error" sx={{ mb: 1 }}>Failed to load supplier exposure.</Alert>}

      {/* FilterBar (Search + Columns) đã bỏ để tiết kiệm không gian — per-column ▽
          filter + resize vẫn ở header. */}
      <Box
        className="app-dataframe"
        sx={{
          zoom,
          border: '1px solid #dfe3e8',
          borderRadius: '8px',
          '& .app-dataframe-toolbar': { background: '#fff', borderBottom: '1px solid #dfe3e8' },
          '& .MuiTableCell-root': {
            fontSize: '0.68rem',
            lineHeight: 1.25,
            borderBottomColor: '#e8ecef',
            whiteSpace: 'nowrap',
            // Auto-width per device + a min floor so columns never wrap to a new
            // line; overflow → horizontal scroll (table minWidth + .app-dataframe
            // overflow). Frozen/resized columns set inline width → override this.
            minWidth: { xs: 78, sm: 88 },
          },
          // Opaque pale gold header (= theme alpha(gold,.18) blended over white,
          // #fdf9ed) + dark forest text — same look as the PnL dashboard, but
          // OPAQUE so scrolling rows don't bleed through. Higher specificity
          // (.MuiTableHead-root ancestor) beats the global cream !important
          // incl. the frozen "Supplier" header cell.
          '& .MuiTableHead-root .app-table-header-cell.MuiTableCell-root, & .MuiTableHead-root .MuiTableCell-head, & .MuiTableHead-root th.app-sticky-col, & .MuiTableHead-root th.app-sticky-col-2': {
            backgroundColor: '#fdf9ed !important',
            backgroundImage: 'none !important',
            color: '#0b3633 !important',
            fontWeight: 800,
            fontSize: '0.66rem',
            borderTop: 'none',
            borderBottom: '1px solid #f0d896',
            boxShadow: 'none',
          },
        }}
      >
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
          {(isLoading || isFetching) && <CircularProgress size={15} />}
          <Typography variant="caption" sx={HIDE_ON_MOBILE_INLINE}>Total: {filteredRows.length}</Typography>
          <Typography variant="caption" sx={HIDE_ON_MOBILE_INLINE}>Loaded: {baseRows.length}</Typography>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={rowsPerPage === -1 ? 0 : page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
            rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
            labelRowsPerPage="Rows"
          />
        </Box>

        {/* Reload (refetch): progress strip + mờ bảng cho tới khi API trả data
            (mirror PnLCard) — trước đây chỉ có spinner 15px nên nhìn như không reload. */}
        <Box sx={{ height: 2 }}>{isFetching && !isLoading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}</Box>
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden', opacity: isFetching && !isLoading ? 0.55 : 1, pointerEvents: isFetching && !isLoading ? 'none' : 'auto', transition: 'opacity 0.15s' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : rows.length === 0 ? (
            <BrandState
              title="No supplier exposure rows"
              description="Try adjusting the search or the column filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <>
                  {/* Tầng 1 — Lv1 group (Supplier rowSpan 2 tại vị trí của nó). */}
                  <TableRow>
                    {headerRuns.map((run) =>
                      run.group === null
                        ? run.cols.map((col) => renderLeafHeaderCell(col, 2))
                        : (
                          <TableCell
                            key={run.cols[0].key}
                            colSpan={run.cols.length}
                            align="center"
                            className="app-table-header-cell"
                            sx={{ ...HEAD_FONT_SX, ...HEAD_SKIN_SX, whiteSpace: 'nowrap', userSelect: 'text' }}
                          >
                            {run.group}
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                  {/* Tầng 2 — Lv2 leaf: sort + ▽ + resize. */}
                  <TableRow>
                    {visibleColumns.filter((c) => c.group).map((col) => renderLeafHeaderCell(col))}
                  </TableRow>
                </>
              )}
              fixedFooterContent={
                totalRow
                  ? () => (
                    <TableRow>
                      {visibleColumns.map((col) => {
                        const key = col.key
                        const isFrozen = FROZEN_KEYS.has(key)
                        const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                        const { text, neg } = col.kind === 'number'
                          ? fmtMeasure(totalRow[key] as number | null)
                          : { text: totalRow.supplierName ?? 'Total', neg: false }
                        return (
                          <TableCell
                            key={key}
                            align={col.align}
                            className={isFrozen ? 'app-sticky-col' : undefined}
                            style={{
                              ...(w ? { width: w, minWidth: w, maxWidth: w } : {}),
                              // `.app-sticky-col` forces white bg → inline override so the
                              // frozen Total cell matches the summary tint (mirror Inventory).
                              ...(isFrozen ? { background: RPT_SUMMARY_BG } : {}),
                            }}
                            sx={{
                              ...BODY_FONT_SX,
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              bgcolor: RPT_SUMMARY_BG,
                              borderBottom: `1px solid ${RPT_GRID}`,
                              borderTop: `1px solid ${RPT_BORDER}`,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: neg ? RPT_NEG : undefined,
                            }}
                          >
                            {text}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                  : undefined
              }
              itemContent={(_: number, row: SupplierExposureRow) =>
                visibleColumns.map((col) => {
                  const key = col.key
                  const isFrozen = FROZEN_KEYS.has(key)
                  const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  if (col.kind !== 'number') {
                    const val = titleCaseName(row.supplierName ?? '')
                    return (
                      <TableCell
                        key={key}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ ...BODY_FONT_SX, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top', fontWeight: 600, bgcolor: '#fff', borderBottom: `1px solid ${RPT_GRID}` }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  const { text, neg } = fmtMeasure(row[key] as number | null)
                  return (
                    <TableCell
                      key={key}
                      align="right"
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{
                        ...BODY_FONT_SX,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'top',
                        bgcolor: '#fff',
                        borderBottom: `1px solid ${RPT_GRID}`,
                        color: neg ? RPT_NEG : undefined,
                      }}
                    >
                      {text}
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%', borderCollapse: 'separate', borderSpacing: 0 }} />,
                // react-virtuoso expects plain HTML section components; MUI types are
                // stricter, so we pass `component` and loosen typing (pattern pilot).
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              }}
            />
          )}
        </Box>

        {/* Global Search popover */}
        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Supplier..."
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        {/* Per-column filter popover (1 instance duy nhất) */}
        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
